import {
  CLICK_DELAY_MS,
  DEFAULT_MIN_POLYGON_VERTICES,
  getSnapDistanceMeters,
  isNearLatLng,
  normalizePathToLiterals,
} from './polygon-drawing.utils';

export type PolygonDrawingCompleteHandler = (
  polygon: google.maps.Polygon,
  path: google.maps.LatLngLiteral[]
) => void;

export interface PolygonDrawingControllerConfig {
  polygonOptions?: google.maps.PolygonOptions;
  minVertices?: number;
  closeDistanceMeters?: number;
  onComplete: PolygonDrawingCompleteHandler;
}

export class PolygonDrawingController {
  private map: google.maps.Map;

  private config: PolygonDrawingControllerConfig;

  private previewPolyline: google.maps.Polyline | null = null;

  private vertexMarkers: google.maps.Marker[] = [];

  private firstVertexClickListener: google.maps.MapsEventListener | null = null;

  private listeners: google.maps.MapsEventListener[] = [];

  private clickTimeout: ReturnType<typeof setTimeout> | null = null;

  private isDrawing = false;

  private previousDisableDoubleClickZoom: boolean | undefined;

  private previousDraggableCursor: string | null | undefined;

  private previousDraggingCursor: string | null | undefined;

  constructor(map: google.maps.Map, config: PolygonDrawingControllerConfig) {
    this.map = map;
    this.config = config;
  }

  start(): void {
    if (this.isDrawing) {
      return;
    }

    this.isDrawing = true;
    this.clearPreview();
    this.previousDisableDoubleClickZoom = this.map.get(
      'disableDoubleClickZoom'
    ) as boolean | undefined;
    this.previousDraggableCursor = this.map.get('draggableCursor') as
      | string
      | null
      | undefined;
    this.previousDraggingCursor = this.map.get('draggingCursor') as
      | string
      | null
      | undefined;
    this.map.setOptions({
      disableDoubleClickZoom: true,
      draggableCursor: 'crosshair',
      draggingCursor: 'crosshair',
    });

    this.listeners.push(
      this.map.addListener('click', (event: google.maps.MapMouseEvent) => {
        if (!event.latLng) {
          return;
        }

        if (this.clickTimeout) {
          clearTimeout(this.clickTimeout);
        }

        const latLng = event.latLng;

        this.clickTimeout = setTimeout(() => {
          this.clickTimeout = null;
          this.handleVertexClick(latLng);
        }, CLICK_DELAY_MS);
      })
    );
  }

  stop(): void {
    this.teardown(true);
  }

  destroy(): void {
    this.teardown(true);
  }

  private getCloseDistanceMeters(): number {
    return this.config.closeDistanceMeters ?? getSnapDistanceMeters(this.map);
  }

  private getStrokeColor(): string {
    return this.config.polygonOptions?.strokeColor ?? '#6301CC';
  }

  private createVertexIcon(isCloseTarget: boolean): google.maps.Symbol {
    const strokeColor = this.getStrokeColor();

    return {
      path: google.maps.SymbolPath.CIRCLE,
      scale: isCloseTarget ? 9 : 6,
      fillColor: isCloseTarget ? strokeColor : '#ffffff',
      fillOpacity: 1,
      strokeColor: isCloseTarget ? '#ffffff' : strokeColor,
      strokeWeight: 2,
    };
  }

  private teardown(clearPreview: boolean): void {
    this.isDrawing = false;
    this.clearListeners();

    if (this.clickTimeout) {
      clearTimeout(this.clickTimeout);
      this.clickTimeout = null;
    }

    if (clearPreview) {
      this.clearPreview();
    }

    this.map.setOptions({
      disableDoubleClickZoom: this.previousDisableDoubleClickZoom,
      draggableCursor: this.previousDraggableCursor ?? undefined,
      draggingCursor: this.previousDraggingCursor ?? undefined,
    });
  }

  private handleVertexClick(latLng: google.maps.LatLng): void {
    const path = this.previewPolyline?.getPath();
    const minVertices = this.config.minVertices ?? DEFAULT_MIN_POLYGON_VERTICES;
    const closeDistance = this.getCloseDistanceMeters();

    if (path && path.getLength() >= minVertices) {
      const firstVertex = path.getAt(0);

      if (isNearLatLng(firstVertex, latLng, closeDistance)) {
        this.completePolygon();
        return;
      }
    }

    if (!this.previewPolyline) {
      this.previewPolyline = new google.maps.Polyline({
        map: this.map,
        path: [latLng],
        editable: false,
        clickable: false,
        geodesic: true,
        strokeColor: this.getStrokeColor(),
        strokeOpacity: this.config.polygonOptions?.strokeOpacity ?? 1,
        strokeWeight: this.config.polygonOptions?.strokeWeight ?? 2,
      });
      this.addVertexMarker(latLng);
      this.updateFirstVertexMarker();
      return;
    }

    path?.push(latLng);
    this.addVertexMarker(latLng);
    this.updateFirstVertexMarker();
  }

  private addVertexMarker(latLng: google.maps.LatLng): void {
    const marker = new google.maps.Marker({
      position: latLng,
      map: this.map,
      clickable: false,
      icon: this.createVertexIcon(false),
      zIndex: 1000,
      optimized: false,
    });

    this.vertexMarkers.push(marker);
  }

  private updateFirstVertexMarker(): void {
    const path = this.previewPolyline?.getPath();
    const minVertices = this.config.minVertices ?? DEFAULT_MIN_POLYGON_VERTICES;
    const canClose = Boolean(path && path.getLength() >= minVertices);
    const firstMarker = this.vertexMarkers[0];

    if (!firstMarker || !path) {
      return;
    }

    firstMarker.setPosition(path.getAt(0));
    firstMarker.setIcon(this.createVertexIcon(canClose));
    firstMarker.setTitle(
      canClose ? 'Clic para cerrar el polígono' : 'Punto inicial'
    );

    if (canClose) {
      firstMarker.setClickable(true);
      firstMarker.setCursor('pointer');

      if (!this.firstVertexClickListener) {
        this.firstVertexClickListener = firstMarker.addListener('click', () => {
          if (this.clickTimeout) {
            clearTimeout(this.clickTimeout);
            this.clickTimeout = null;
          }

          this.completePolygon();
        });
      }

      return;
    }

    firstMarker.setClickable(false);
    firstMarker.setCursor(undefined);

    if (this.firstVertexClickListener) {
      google.maps.event.removeListener(this.firstVertexClickListener);
      this.firstVertexClickListener = null;
    }
  }

  private completePolygon(): void {
    const path = this.previewPolyline?.getPath();
    const minVertices = this.config.minVertices ?? DEFAULT_MIN_POLYGON_VERTICES;

    if (!path || path.getLength() < minVertices) {
      return;
    }

    const vertices = path.getArray();
    const literals = normalizePathToLiterals(vertices);

    this.clearPreview();

    const polygon = new google.maps.Polygon({
      editable: false,
      clickable: false,
      ...this.config.polygonOptions,
      map: this.map,
      paths: vertices,
    });

    this.teardown(false);
    this.config.onComplete(polygon, literals);
  }

  private clearPreview(): void {
    this.previewPolyline?.setMap(null);
    this.previewPolyline = null;

    if (this.firstVertexClickListener) {
      google.maps.event.removeListener(this.firstVertexClickListener);
      this.firstVertexClickListener = null;
    }

    this.vertexMarkers.forEach(marker => marker.setMap(null));
    this.vertexMarkers = [];
  }

  private clearListeners(): void {
    this.listeners.forEach(listener => {
      google.maps.event.removeListener(listener);
    });
    this.listeners = [];
  }
}
